/**
 * "Show returns from …" — an admin can pick, per client, the first month the
 * return charts draw (clients.returns_from_month). Everything here is display
 * arithmetic over series the server already returned; no stored number moves.
 *
 * The rule: the month before the chosen one is the new zero. A cumulative
 * series is re-based on that month's value, so the first shown month's
 * cumulative return is simply its own monthly return — the way a fund sheet
 * starts from a base row at 0. Months before the chosen one are not drawn.
 */
import type { PortfolioMonth, PortfolioYearStat } from './components/PortfolioSummaryCharts.js';

/** 'YYYY-MM', or null = from the beginning (today's behaviour). */
export type ReturnsFrom = string | null;

const monthOf = (date: string) => date.slice(0, 7);

/** Rows dated in or after the chosen month, in date order. All rows when nothing is chosen. */
export function fromMonth<T extends { date: string }>(rows: T[], from: ReturnsFrom): T[] {
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  return from ? sorted.filter((r) => monthOf(r.date) >= from) : sorted;
}

/**
 * Keep the rows from the chosen month on, with each cumulative key re-based so
 * the last row before that month counts as zero: (1 + c) / (1 + base) − 1.
 * A key that is null on a row stays null; a missing base counts as zero.
 */
export function rebaseFrom<T extends { date: string }>(rows: T[], from: ReturnsFrom, keys: readonly (keyof T)[]): T[] {
  if (!from) return rows;
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  const base = sorted.filter((r) => monthOf(r.date) < from).at(-1);
  return sorted
    .filter((r) => monthOf(r.date) >= from)
    .map((r) => {
      const out = { ...r };
      for (const k of keys) {
        const v = r[k];
        const b = base?.[k];
        if (typeof v === 'number') out[k] = ((1 + v) / (1 + (typeof b === 'number' ? b : 0)) - 1) as T[typeof k];
      }
      return out;
    });
}

/**
 * The whole-portfolio chart's input from the chosen month on: months re-based,
 * and the yearly bars worked out over the shown months only — so a year that
 * starts mid-way is the compounding of the months actually drawn, never of
 * months the client cannot see. Nothing is chosen → the server's own figures.
 */
export function portfolioFrom(
  months: PortfolioMonth[],
  years: PortfolioYearStat[],
  from: ReturnsFrom,
): { months: PortfolioMonth[]; years: PortfolioYearStat[] } {
  if (!from) return { months, years };
  const shown = rebaseFrom(months, from, ['cumulative_return', 'benchmark_cumulative']);
  const byYear = new Map<string, { p: number; b: number; hasP: boolean; hasB: boolean }>();
  for (const m of shown) {
    const y = m.date.slice(0, 4);
    if (!byYear.has(y)) byYear.set(y, { p: 1, b: 1, hasP: false, hasB: false });
    const s = byYear.get(y)!;
    if (m.monthly_return !== null) {
      s.p *= 1 + m.monthly_return;
      s.hasP = true;
    }
    if (m.benchmark_monthly !== null) {
      s.b *= 1 + m.benchmark_monthly;
      s.hasB = true;
    }
  }
  const shownYears = [...byYear.entries()]
    .filter(([, s]) => s.hasP)
    .map(([year, s]) => ({ year, portfolio: s.p - 1, benchmark: s.hasB ? s.b - 1 : null }))
    .sort((a, b) => a.year.localeCompare(b.year));
  return { months: shown, years: shownYears };
}
